import { ColorSchemeScript, MantineProvider } from '@mantine/core';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import type { LoaderFunctionArgs } from 'react-router';
import type { MetaFunction } from 'react-router';
import {
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  data,
  useLoaderData,
  useRouteError,
} from 'react-router';
import { EqutumToast, NegativeToast, PositiveToast } from './components/equtum_toast';
import EqutumLogoHorizontal from './images/equtum_logo_h.svg?react';
import './print.css';
import './tailwind.css';
import type { ToastMessage } from './types';
import { storage } from './utils/session.server';

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await storage.getSession(request.headers.get('Cookie'));
  const toastMessage = session.get('toastMessage') as ToastMessage | undefined;

  return data(
    {
      toastMessageFromSession: toastMessage,
      ENV: {
        BACKEND_URL: process.env.BACKEND_URL,
      },
    },
    {
      headers: {
        'Set-Cookie': await storage.commitSession(session),
      },
    }
  );
}

export const meta: MetaFunction = () => {
  return [{ title: 'EQUTUM' }];
};

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useLoaderData<typeof loader>();
  const toastMessageFromSession = data?.toastMessageFromSession;
  const ENV = data?.ENV;

  useEffect(() => {
    const toastData =
      toastMessageFromSession || JSON.parse(sessionStorage.getItem('toastMessage') || 'null');

    if (toastData) {
      const showToast =
        toastData.type === 'success'
          ? () => toast.success(<PositiveToast message={toastData.content} />)
          : () => toast.error(<NegativeToast message={toastData.content} />);

      showToast();

      if (!toastMessageFromSession) {
        sessionStorage.removeItem('toastMessage');
      }
    }
  }, [toastMessageFromSession]);

  return (
    <html data-mantine-color-scheme="light" lang="ja">
      <head>
        <meta charSet="utf-8" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <Meta />
        <Links />
        <ColorSchemeScript />
        <meta content="noindex, nofollow" name="robots" />
        <meta content="noindex, nofollow" name="googlebot" />
      </head>
      <body className="font-body">
        <EqutumToast />
        <MantineProvider>{children}</MantineProvider>
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(ENV)}`,
          }}
        />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  console.error(error);

  const containerStyle = {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
  };

  const contentStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
  };

  const messageStyle = {
    textAlign: 'center' as const,
    fontWeight: 500,
    marginTop: '24px',
    marginBottom: '8px',
  };

  const linkStyle = {
    textAlign: 'center' as const,
    fontWeight: 600,
    color: '#4F46E5',
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        <EqutumLogoHorizontal height={100} width={320} />
        <div style={messageStyle}>エラーが発生しました</div>
        <div style={linkStyle}>
          <Link to="/">トップページに戻る</Link>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  return <Outlet />;
}
